import {
  Button,
  Container,
  Flex,
  Heading,
  Section,
  Square,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { publish } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import React, { useEffect, useState } from 'react';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { SnackbarStatus } from 'src/components/snackbar/SnackbarLauncher';
import {
  IVariant,
  IVariants,
  useVariants,
} from 'src/components/VariantsProvider/VariantsProvider';
import PageSection from 'src/components/enterprise/layout/PageSection';

interface IGroupedVariant {
  [page: string]: IVariant[];
}

export function VariantSelection(): JSX.Element {
  const [variantsCache, setVariantsCache] = useState(null);
  const [groupedVariants, setGroupedVariants] = useState(null);

  const {
    overrideVariants,
    combinedVariants,
    hasOverrides,
    cleanOverriddenVariants,
  } = useVariants();

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedVariants, hasOverrides]);

  // Groups variants that has same page value so the can by discriminated in the view by the page that variant belongs to
  function getGroupedVariants(variantsCache: IVariants): IGroupedVariant {
    const groupedVariantsCache: IGroupedVariant = {};
    Object.keys(variantsCache).forEach((variantKey) => {
      const currentVariant = variantsCache[variantKey];
      if (groupedVariantsCache[currentVariant.page]) {
        groupedVariantsCache[currentVariant.page].push(currentVariant);
      } else {
        groupedVariantsCache[currentVariant.page] = [currentVariant];
      }
    });
    return groupedVariantsCache;
  }
  // keeps the non-grouped list of variants updated
  function rebuildVariant(): void {
    Object.keys(groupedVariants).forEach((group) => {
      if (groupedVariants[group]) {
        groupedVariants[group].forEach((variant) => {
          Object.keys(variantsCache).forEach((key) => {
            if (
              variantsCache[key].page === variant.page &&
              variantsCache[key].description === variant.description
            ) {
              variantsCache[key] = variant;
            }
          });
        });
      }
    });

    setVariantsCache(variantsCache);
  }

  function saveOverrides(): void {
    rebuildVariant();
    overrideVariants({ ...variantsCache });
    publish('push:snackbar', {
      status: SnackbarStatus.SUCCESS,
      message: 'Overrides were saved!',
    });
  }

  function cleanOverrides() {
    cleanOverriddenVariants();
    publish('push:snackbar', {
      status: SnackbarStatus.SUCCESS,
      message: 'Overrides were removed!',
    });
  }

  // changes variant selected value based on checkbox interaction
  function onSelectedChange(page: string, variant: IVariant) {
    const currentGroupedVariants = { ...groupedVariants };
    const currentVariantsFromPage = currentGroupedVariants[
      variant.page
    ] as IVariant[];
    const index = currentVariantsFromPage.findIndex(
      (v) => v.description === variant.description
    );

    currentVariantsFromPage[index].selected =
      !currentVariantsFromPage[index].selected;
    currentGroupedVariants[variant.page] = currentVariantsFromPage;
    setGroupedVariants(currentGroupedVariants);
  }

  function init() {
    const groupedVariants = getGroupedVariants(combinedVariants || {});
    setVariantsCache(combinedVariants);
    setGroupedVariants(groupedVariants);
  }

  return (
    <Container variant="default">
      <Section inset="md">
        <Stack y="md">
          <Heading variant="h1">Variant configuration</Heading>
        </Stack>
        <Stack y="md">
          <PageSection>
            <>
              {groupedVariants &&
                Object.keys(groupedVariants).map((pageKey) => {
                  const variants = groupedVariants[pageKey];

                  return (
                    <Stack y="md" key={pageKey}>
                      <Stack y="sm">
                        <Heading variant="h6">{pageKey}</Heading>
                      </Stack>
                      <Square>
                        {variants.map((variant: IVariant) => (
                          <Flex key={variant.description}>
                            <Checkbox
                              id={variant.description}
                              name={variant.key}
                              checked={variant.selected}
                              ariaLabel={variant.description}
                              label={variant.description}
                              onChange={() =>
                                onSelectedChange(pageKey, variant)
                              }
                            />
                          </Flex>
                        ))}
                      </Square>
                    </Stack>
                  );
                })}

              <Flex>
                <Stack x="lg">
                  <Button variant="primary" onClick={saveOverrides}>
                    Save overrides
                  </Button>
                </Stack>
                {hasOverrides && (
                  <Button variant="secondary" onClick={cleanOverrides}>
                    Clean local overrides
                  </Button>
                )}
              </Flex>
            </>
          </PageSection>
        </Stack>
      </Section>
    </Container>
  );
}
